import React, { useEffect, useState, useContext } from "react";
import { Container, Row, Col, Card, Spinner } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import APIServicenew from "../utils/APIGeneralService";
import { UserContext } from "../context/UserContextProvider";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const TestCaseImages = () => {
  const APIServices = new APIServicenew(process.env.REACT_APP_OTHER_API_URL);
  const { accessToken, logout } = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get data passed from TestRunView
  const { runId, executionId } = location.state || {};
  
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);

  const headers = {
    Authorization: `Bearer ${accessToken}`,
  };

  useEffect(() => {
    if (!runId || !executionId) {
      toast.error("No folder path provided");
      navigate(-1);
      return;
    }
    fetchImages();
  }, [runId, executionId]);

  const fetchImages = async () => {
    setLoading(true);
    try {
      const response = await APIServices.get(
        `gcp/list-folder?folder=RUN-${runId}/${executionId}`,
        headers
      );

      if (response?.status === 200) {
        console.log("Images response:", response.data);
        
        const imageList = response.data.images || response.data;
        setImages(Array.isArray(imageList) ? imageList : []);
      } else if (response?.status === 401) {
        logout();
        navigate("/");
      }
    } catch (error) {
      console.error("Error fetching images:", error);
      toast.error("Failed to load images");
    } finally {
      setLoading(false);
    }
  };

  const extractFileName = (url) => {
    try {
      const urlParts = url.split('/');
      const fileNameWithParams = urlParts[urlParts.length - 1];
      const fileName = fileNameWithParams.split('?')[0];
      return fileName;
    } catch (error) {
      return "image.jpg";
    }
  };

  // Navigation functions for slider
  const handlePrevious = () => {
    setSelectedImageIndex(prev => (prev > 0 ? prev - 1 : images.length - 1));
  };

  const handleNext = () => {
    setSelectedImageIndex(prev => (prev < images.length - 1 ? prev + 1 : 0));
  };

  const handleKeyDown = (e) => {
    if (selectedImageIndex !== null) {
      if (e.key === 'ArrowLeft') {
        handlePrevious();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      } else if (e.key === 'Escape') {
        setSelectedImageIndex(null);
      }
    }
  };

  // Add event listener for keyboard navigation
  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedImageIndex]);

  return (
    <div className="all_content test_case_images">
      <Container fluid>
        {/* Header */}
        <div className="head-section d-flex align-items-center justify-content-between mb-4">
          <div>
            <h1 className="font_16 font-600 clr_blue">
              Test Case Images
            </h1>
          </div>

          <div className="d-flex gap-2">
            <button
              className="btn d-flex align-items-center gap-2 font_14 clr_sky"
              onClick={() => navigate(-1)}
            >
              <i className="fas fa-long-arrow-alt-left"></i> Back
            </button>
          </div>
        </div>

        {/* Images Grid */}
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">Loading images...</p>
          </div>
        ) : images.length === 0 ? (
          <div className="text-center py-5">
            <i className="fa-solid fa-image fa-3x clr_grey mb-3"></i>
            <p className="font_16 clr_grey">No images found in this folder</p>
          </div>
        ) : (
          <>
            <p className="font_14 mb-3">
              Found {images.length} image{images.length > 1 ? 's' : ''}
            </p>
            <Row className="g-4">
              {images.map((imageUrl, index) => {
                const fileName = extractFileName(imageUrl);
                return (
                  <Col key={index} xs={12} sm={6} md={4} lg={3}>
                    <Card className="image-card h-100">
                      <div 
                        className="image-container p-2 d-flex align-items-center justify-content-center"
                        style={{ 
                          height: '200px', 
                          cursor: 'pointer',
                          backgroundColor: '#f8f9fa'
                        }}
                        onClick={() => setSelectedImageIndex(index)}
                      >
                        <img
                          src={imageUrl}
                          alt={fileName}
                          style={{ 
                            maxWidth: '100%', 
                            maxHeight: '100%', 
                            objectFit: 'contain' 
                          }}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'https://via.placeholder.com/200?text=Image+Error';
                          }}
                          loading="lazy"
                        />
                      </div>
                      <Card.Body className="p-2">
                        <p className="font_12 mb-0 text-truncate" title={fileName}>
                          {fileName}
                        </p>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          </>
        )}

        {/* Image Slider Modal */}
        {selectedImageIndex !== null && (
          <div 
            className="modal show d-block"
            style={{ 
              position: 'fixed', 
              top: 0, 
              left: 0, 
              right: 0, 
              bottom: 0, 
              backgroundColor: 'rgba(0,0,0,0.95)',
              zIndex: 1050
            }}
            onClick={() => setSelectedImageIndex(null)}
          >
            <div 
              className="modal-dialog modal-xl modal-dialog-centered m-0"
              style={{ maxWidth: '95vw', height: '100vh', margin: '0 auto' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-content bg-transparent border-0 h-100">
                {/* Header with counter and close button */}
                <div className="modal-header border-0 position-absolute top-0 start-0 end-0" style={{ zIndex: 1060 }}>
                  <h5 className="modal-title text-white">
                    {extractFileName(images[selectedImageIndex])} 
                    <span className="ms-2 text-white-50">
                      ({selectedImageIndex + 1} of {images.length})
                    </span>
                  </h5>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={() => setSelectedImageIndex(null)}
                  ></button>
                </div>

                {/* Navigation buttons */}
                <button
                  className="btn position-absolute top-50 start-0 translate-middle-y text-white"
                  style={{ 
                    fontSize: '3rem', 
                    background: 'rgba(0,0,0,0.5)',
                    border: 'none',
                    padding: '1rem 1.5rem',
                    zIndex: 1060,
                    marginLeft: '20px'
                  }}
                  onClick={handlePrevious}
                >
                  <i className="fas fa-chevron-left"></i>
                </button>

                <button
                  className="btn position-absolute top-50 end-0 translate-middle-y text-white"
                  style={{ 
                    fontSize: '3rem', 
                    background: 'rgba(0,0,0,0.5)',
                    border: 'none',
                    padding: '1rem 1.5rem',
                    zIndex: 1060,
                    marginRight: '20px'
                  }}
                  onClick={handleNext}
                >
                  <i className="fas fa-chevron-right"></i>
                </button>

                {/* Image container */}
                <div className="modal-body d-flex align-items-center justify-content-center p-0" style={{ height: '100vh' }}>
                  <img
                    src={images[selectedImageIndex]}
                    alt={extractFileName(images[selectedImageIndex])}
                    style={{ 
                      maxWidth: '90%', 
                      maxHeight: '90vh',
                      borderRadius: '4px',
                      objectFit: 'contain'
                    }}
                  />
                </div>

                {/* Thumbnail strip at bottom */}
                <div className="position-absolute bottom-0 start-0 end-0 p-3" style={{ 
                  background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
                  zIndex: 1060
                }}>
                  <div className="d-flex justify-content-center gap-2 overflow-auto py-2" style={{ maxWidth: '100%' }}>
                    {images.map((thumbUrl, idx) => (
                      <div
                        key={idx}
                        onClick={() => setSelectedImageIndex(idx)}
                        style={{
                          width: '60px',
                          height: '60px',
                          cursor: 'pointer',
                          border: idx === selectedImageIndex ? '3px solid #0d6efd' : '3px solid transparent',
                          borderRadius: '4px',
                          overflow: 'hidden',
                          flexShrink: 0
                        }}
                      >
                        <img
                          src={thumbUrl}
                          alt={`Thumbnail ${idx + 1}`}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Container>
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default TestCaseImages;